import {toFixed} from '@/helper/num';
import type {FormatedResult, Currency, Network, Token} from './shared';

export function formatNetworks(networks: Array<Network>): FormatedResult {
  const {networkCurrencyOptionsMap, eachToken: eachTokenForGetNetworkCurrencyOptionsMap} = getNetworkCurrencyOptionsMap(networks);
  const {currencyNetworkMap, eachToken: eachTokenForGetCurrencyNetworkMap} = getCurrencyNetworkMap();
  const {tokenMap, eachToken: eachTokenForGetTokenMap} = getTokenMap();
  const {contractAddressTokenMap, eachToken: eachTokenForGetContractAddressTokenMap} = getContractAddressTokenMap();

  const chainIdNetworkMap: FormatedResult['chainIdNetworkMap'] = {};
  const chainNameNetworkMap: FormatedResult['chainNameNetworkMap'] = {};
  const networkSupportCurrenciesMap: FormatedResult['networkSupportCurrenciesMap'] = {};
  const currencyNameCurrencyMap: FormatedResult['currencyNameCurrencyMap'] = {};

  networks.forEach((network) => {
    const {currencyList = [], ...networkConfig} = network;
    chainIdNetworkMap[network.chainId] = network;
    chainNameNetworkMap[network.chainName] = network;
    networkSupportCurrenciesMap[network.extNetWorkId] = currencyList;

    currencyList.forEach((currency) => {
      try {
        if (currency.balance) {
          currency.balance = Number(toFixed(currency.balance, currency.withdrawPrecision || 8)) + '';
        }
      } catch (error) {}
      const token = {...currency, ...networkConfig};

      currencyNameCurrencyMap[currency.currency] = currency;
      const eachTokenParams = {network, currency, token};
      eachTokenForGetNetworkCurrencyOptionsMap(eachTokenParams);
      eachTokenForGetCurrencyNetworkMap(eachTokenParams);
      eachTokenForGetTokenMap(eachTokenParams);
      eachTokenForGetContractAddressTokenMap(eachTokenParams);
    });
  });

  return {
    networkCurrencyOptionsMap,
    networkSupportCurrenciesMap,
    currencyNetworkMap,
    tokenMap,
    contractAddressTokenMap,
    chainIdNetworkMap,
    chainNameNetworkMap,
    originData: networks,
    currencyNameCurrencyMap,
  };
}

function getTokenMap() {
  const tokenMap: FormatedResult['tokenMap'] = {};

  function eachToken({network, currency, token}: EachTokenParams) {
    const {currency: currencyName} = currency;
    const {currencyList, ...networkConfig} = network;
    const currency_network = `${currencyName}_${networkConfig.extNetWorkId}`;
    tokenMap[currency_network] = token;
  }

  return {
    tokenMap,
    eachToken,
  };
}

function getCurrencyNetworkMap() {
  const currencyNetworkMap: FormatedResult['currencyNetworkMap'] = {};

  function eachToken({network, currency, token}: EachTokenParams) {
    const {currency: currencyName} = currency;
    const {currencyList, ...networkConfig} = network;
    const currencyNetwork = currencyNetworkMap[currencyName];
    if (currencyNetwork) {
      currencyNetwork.push(networkConfig);
    } else {
      currencyNetworkMap[currencyName] = [networkConfig];
    }
  }
  return {
    currencyNetworkMap,
    eachToken,
  };
}

function getNetworkCurrencyOptionsMap(networks: Array<Network>) {
  const networkCurrencyOptionsMap: FormatedResult['networkCurrencyOptionsMap'] = {};
  // 临时数据：用来判断每条链上对应的currency是否重复
  const networkCurrencyOptionsIndexMap: Record<Network['extNetWorkId'], Array<Currency['currency']>> = {};

  networks.forEach((network) => {
    const {extNetWorkId, currencyList = []} = network;
    networkCurrencyOptionsMap[extNetWorkId] = [];
    networkCurrencyOptionsIndexMap[extNetWorkId] = [];

    // 优先存放当前链的币
    currencyList.forEach((currency) => {
      const option = createCurrencyOption(extNetWorkId, currency, network);
      networkCurrencyOptionsIndexMap[extNetWorkId].push(currency.currency);
      networkCurrencyOptionsMap[extNetWorkId].push(option);
    });
  });

  function eachToken({network, currency, token}: EachTokenParams) {
    const {extNetWorkId} = network;
    const {currency: currencyName} = currency;
    for (const networkId in networkCurrencyOptionsMap) {
      const isCurrentChain = extNetWorkId === networkId;

      // 只存放非本链的币，以及还未放进去的币
      if (!isCurrentChain) {
        const networkCurrencyExit = networkCurrencyOptionsIndexMap[networkId].includes(currencyName);
        if (!networkCurrencyExit) {
          const option = createCurrencyOption(networkId, currency, network);
          networkCurrencyOptionsMap[networkId].push(option);
          networkCurrencyOptionsIndexMap[networkId].push(currencyName);
        }
      }
    }
  }

  return {
    networkCurrencyOptionsMap,
    eachToken,
  };
}

function createCurrencyOption(networkId: Network['extNetWorkId'], currency: Currency, network: Network): FormatedResult['networkCurrencyOptionsMap'][string][number] {
  let balanceValue;
  const {currency: currencyName, balance} = currency;
  try {
    if (balance) {
      balanceValue = Number(toFixed(balance, currency.withdrawPrecision || 8)) + '';
    }
  } catch (error) {}
  const {extNetWorkId, showBalance} = network;
  const isCurrentChain = extNetWorkId === networkId;
  const option = {text: currencyName, value: currencyName, balance: balanceValue, isCurrentChain, showBalance};
  return option;
}

function getContractAddressTokenMap() {
  const contractAddressTokenMap: FormatedResult['contractAddressTokenMap'] = {};

  function eachToken({network, currency, token}: EachTokenParams) {
    const {contractName} = currency;
    if (contractName) {
      contractAddressTokenMap[contractName] = token;
    }
  }

  return {
    contractAddressTokenMap,
    eachToken,
  };
}

interface EachTokenParams {
  currency: Currency;
  network: Network;
  token: Token;
}
